import React, { useState, useMemo } from 'react';
import {
  Paper, Typography, Box, Avatar, Chip, Grid, Skeleton, Alert,
  Pagination, Select, MenuItem, FormControl, InputLabel,
  Stack, Button, IconButton, Tooltip, Snackbar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ViewList, ViewModule, Share, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const RecentClassifications = ({ history, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const navigate = useNavigate();

  const getCategoryColor = (category) => {
    const colors = {
      'Recyclable': '#43a047',
      'Organic': '#66bb6a',
      'Hazardous': '#e53935',
      'General': '#546e7a'
    };
    return colors[category] || '#546e7a';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Recyclable': '♻️',
      'Organic': '🌱',
      'Hazardous': '⚠️',
      'General': '🗑️'
    };
    return icons[category] || '📦';
  };

  // Pagination logic
  const paginatedHistory = useMemo(() => {
    if (!history || history.length === 0) return { data: [], totalPages: 0 };

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const data = history.slice(startIndex, endIndex);
    const totalPages = Math.ceil(history.length / itemsPerPage);

    return { data, totalPages };
  }, [history, currentPage, itemsPerPage]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page
  };

  // Handle view details - navigate to classification details page
  const handleViewDetails = (classificationId) => {
    navigate(`/classification/${classificationId}`);
  };

  // Handle share classification
  const handleShare = async (classification) => {
    try {
      const shareUrl = `${window.location.origin}/shared-classification/${classification.shareToken}`;
      const shareText = `Check out my waste classification result on Sortify! 🌱\n\nCategory: ${classification.category?.name || 'Unknown'}\nConfidence: ${Math.round(classification.resultConfidence || 0)}%\n\n${shareUrl}`;

      if (navigator.share) {
        // Use native share API if available (mobile devices)
        await navigator.share({
          title: 'My Waste Classification - Sortify',
          text: shareText,
          url: shareUrl
        });
      } else {
        // Fallback to clipboard copy
        await navigator.clipboard.writeText(shareText);
        setSnackbar({
          open: true,
          message: '🔗 Share link copied to clipboard! Share your eco achievement!'
        });
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setSnackbar({
        open: true,
        message: '❌ Failed to share. Please try again.'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  if (loading) {
    return (
      <Paper elevation={4} sx={{ p: 4, borderRadius: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          📈 Recent Classifications
        </Typography>
        <Grid container spacing={2}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper elevation={4} sx={{
      p: 4,
      borderRadius: 4,
      background: 'linear-gradient(135deg, #ffffff, #f5fbf7)',
      border: '2px solid #43a04715',
      minHeight: '600px'
    }}>
      {/* Header with Controls */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" fontWeight={800} sx={{
          color: '#43a047',
          background: 'linear-gradient(135deg, #43a047, #66bb6a)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          📈 Recent Classifications
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          {/* Items per page selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Show</InputLabel>
            <Select
              value={itemsPerPage}
              label="Show"
              onChange={handleItemsPerPageChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderColor: '#43a047',
                }
              }}
            >
              <MenuItem value={6}>6 items</MenuItem>
              <MenuItem value={12}>12 items</MenuItem>
              <MenuItem value={18}>18 items</MenuItem>
              <MenuItem value={24}>24 items</MenuItem>
            </Select>
          </FormControl>

          {/* View mode toggle */}
          <Box sx={{
            display: 'flex',
            border: '2px solid #43a047',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <Tooltip title="Grid View">
              <IconButton
                onClick={() => setViewMode('grid')}
                sx={{
                  backgroundColor: viewMode === 'grid' ? '#43a047' : 'transparent',
                  color: viewMode === 'grid' ? 'white' : '#43a047',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: viewMode === 'grid' ? '#388e3c' : '#43a04720'
                  }
                }}
              >
                <ViewModule />
              </IconButton>
            </Tooltip>
            <Tooltip title="List View">
              <IconButton
                onClick={() => setViewMode('list')}
                sx={{
                  backgroundColor: viewMode === 'list' ? '#43a047' : 'transparent',
                  color: viewMode === 'list' ? 'white' : '#43a047',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: viewMode === 'list' ? '#388e3c' : '#43a04720'
                  }
                }}
              >
                <ViewList />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Box>

      {/* Stats Summary */}
      {history && history.length > 0 && (
        <Box sx={{
          mb: 3,
          p: 2,
          background: 'linear-gradient(135deg, #43a04710, #66bb6a10)',
          borderRadius: 3,
          border: '1px solid #43a04730'
        }}>
          <Typography variant="body1" color="#43a047" fontWeight={600}>
            📊 Showing {paginatedHistory.data.length} of {history.length} total classifications
          </Typography>
        </Box>
      )}

      {/* Content */}
      {!history || history.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          py: 8,
          background: 'linear-gradient(135deg, #f0f9f0, #e8f5e8)',
          borderRadius: 4,
          border: '2px dashed #43a047'
        }}>
          <Typography variant="h1" sx={{ fontSize: '6rem', mb: 2, opacity: 0.7 }}>🔍</Typography>
          <Typography variant="h5" fontWeight={700} color="#43a047" gutterBottom>
            No Classifications Yet
          </Typography>
          <Typography variant="h6" color="#546e7a" sx={{ mb: 3 }}>
            Start your eco journey by classifying your first waste item!
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #43a047, #66bb6a)',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #388e3c, #43a047)',
                transform: 'translateY(-2px)'
              }
            }}
            href="/classify"
          >
            🚀 Start Classifying
          </Button>
        </Box>
      ) : (
        <>
          {/* Classifications Grid/List */}
          <Grid container spacing={viewMode === 'grid' ? 3 : 2}>
            {paginatedHistory.data.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={viewMode === 'grid' ? 6 : 12}
                md={viewMode === 'grid' ? 4 : 12}
                key={item.id || index}
              >
                <Paper elevation={3} sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(getCategoryColor(item.category?.name), 0.05)}, #ffffff)`,
                  border: `2px solid ${alpha(getCategoryColor(item.category?.name), 0.15)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.02)',
                    boxShadow: `0 16px 40px ${alpha(getCategoryColor(item.category?.name), 0.2)}`,
                    border: `2px solid ${getCategoryColor(item.category?.name)}`
                  }
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    flexDirection: viewMode === 'list' ? 'row' : 'column',
                    textAlign: viewMode === 'list' ? 'left' : 'center'
                  }}>
                    <Avatar
                      src={item.imageUrl}
                      sx={{
                        width: viewMode === 'list' ? 60 : 70,
                        height: viewMode === 'list' ? 60 : 70,
                        mr: viewMode === 'list' ? 3 : 0,
                        mb: viewMode === 'list' ? 0 : 2,
                        border: `3px solid ${getCategoryColor(item.category?.name)}`,
                        boxShadow: `0 8px 24px ${alpha(getCategoryColor(item.category?.name), 0.3)}`,
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1) rotate(5deg)'
                        }
                      }}
                    >
                      <Typography sx={{ fontSize: '2rem' }}>
                        {getCategoryIcon(item.category?.name)}
                      </Typography>
                    </Avatar>

                    <Box sx={{ flex: 1 }}>
                      <Chip
                        icon={<span>{getCategoryIcon(item.category?.name)}</span>}
                        label={item.category?.name || 'Unknown'}
                        sx={{
                          background: `linear-gradient(135deg, ${getCategoryColor(item.category?.name)}, ${alpha(getCategoryColor(item.category?.name), 0.8)})`,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          mb: viewMode === 'list' ? 0 : 1,
                          boxShadow: `0 4px 12px ${alpha(getCategoryColor(item.category?.name), 0.3)}`
                        }}
                      />

                      {viewMode === 'list' && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                          <Tooltip title="View Details">
                            <IconButton size="small" sx={{ color: '#43a047' }} onClick={() => handleViewDetails(item.id)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Share Classification">
                            <IconButton size="small" sx={{ color: '#43a047' }} onClick={() => handleShare(item)}>
                              <Share fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="#546e7a" gutterBottom sx={{ fontWeight: 600 }}>
                      🎯 Confidence:
                      <Chip
                        label={`${Math.round(item.resultConfidence || 0)}%`}
                        size="small"
                        sx={{
                          ml: 1,
                          background: item.resultConfidence > 90 ? '#43a047' : item.resultConfidence > 70 ? '#ff9800' : '#f44336',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Typography>

                    {item.suggestion && (
                      <Box sx={{
                        background: `linear-gradient(135deg, ${alpha(getCategoryColor(item.category?.name), 0.1)}, ${alpha(getCategoryColor(item.category?.name), 0.05)})`,
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(getCategoryColor(item.category?.name), 0.2)}`,
                        mt: 1
                      }}>
                        <Typography variant="body2" sx={{
                          color: getCategoryColor(item.category?.name),
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          lineHeight: 1.4
                        }}>
                          💡 {item.suggestion}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 1,
                    borderTop: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="caption" color="#9e9e9e" sx={{ fontWeight: 600 }}>
                      📅 {new Date(item.classifiedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>

                    {viewMode === 'grid' && (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Details">
                          <IconButton size="small" sx={{ color: '#43a047' }} onClick={() => handleViewDetails(item.id)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share">
                          <IconButton size="small" sx={{ color: '#43a047' }} onClick={() => handleShare(item)}>
                            <Share fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {paginatedHistory.totalPages > 1 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
              p: 2
            }}>
              <Pagination
                count={paginatedHistory.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 600,
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #388e3c, #43a047)'
                      }
                    }
                  }
                }}
              />
            </Box>
          )}

          {/* Footer Stats */}
          <Box sx={{
            mt: 3,
            p: 2,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f8fffe, #f0f9f0)',
            borderRadius: 3,
            border: '1px solid #e8f5e8'
          }}>
            <Typography variant="body2" color="#43a047" fontWeight={600}>
              🌍 You've helped classify {history.length} waste items! Keep up the great eco work!
              {history.length >= 10 && ' 🏆'}
              {history.length >= 50 && ' You\'re becoming an eco leader! 🌟'}
              {history.length >= 100 && ' Eco Master level achieved! 👑'}
            </Typography>
          </Box>

          {/* Snackbar for share feedback */}
          <Snackbar
            open={snackbar.open}
            onClose={handleCloseSnackbar}
            message={snackbar.message}
            autoHideDuration={4000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ width: '100%', maxWidth: 600 }}
          />
        </>
      )}
    </Paper>
  );
};

export default RecentClassifications;
